import type { AttendanceCorrectionBreak } from '../types/attendanceCorrection';
import type { AttendanceDetails, AttendanceEvent } from '../types/attendance';
import { parseClockToMinutes, timestampToClock } from './checkoutBreak';

export type AttendanceCorrectionBreakDraft = {
  key: string;
  startTime: string;
  endTime: string;
  startLocked: boolean;
  endLocked: boolean;
  /** Recorded times this row was prefilled from; null for a new break. */
  existingStartTime: string | null;
  existingEndTime: string | null;
};

/** A recorded break the employee removed, by its recorded times. */
export type AttendanceCorrectionRemovedBreak = {
  startTime: string;
  endTime: string | null;
};

export type AttendanceCorrectionDraft = {
  blocked: boolean;
  blockReason: string | null;
  checkInLocked: boolean;
  checkOutLocked: boolean;
  breakStartLocked: boolean;
  breakEndLocked: boolean;
  checkInTime: string;
  checkOutTime: string;
  breakStartTime: string;
  breakEndTime: string;
  breaks: AttendanceCorrectionBreakDraft[];
  removedBreaks: AttendanceCorrectionRemovedBreak[];
};

function clockFromEvent(event: AttendanceEvent | undefined): string {
  if (!event) return '';
  return (
    timestampToClock(event.localTimestamp) ??
    timestampToClock(event.timestamp) ??
    ''
  );
}

function firstEvent(
  events: AttendanceEvent[],
  eventType: string,
): AttendanceEvent | undefined {
  return events.find(
    event => (event.eventType || '').toUpperCase() === eventType,
  );
}

function emptyBreak(key: string): AttendanceCorrectionBreakDraft {
  return {
    key,
    startTime: '',
    endTime: '',
    startLocked: false,
    endLocked: false,
    existingStartTime: null,
    existingEndTime: null,
  };
}

function existingBreak(
  key: string,
  startTime: string,
  endTime: string,
): AttendanceCorrectionBreakDraft {
  return {
    key,
    startTime,
    endTime,
    startLocked: false,
    endLocked: false,
    existingStartTime: startTime || null,
    existingEndTime: endTime || null,
  };
}

export function breakPairsFromEvents(
  events: AttendanceEvent[],
): AttendanceCorrectionBreakDraft[] {
  const ordered = [...events].sort((left, right) => {
    const leftKey = left.localTimestamp ?? left.timestamp ?? '';
    const rightKey = right.localTimestamp ?? right.timestamp ?? '';
    return leftKey < rightKey ? -1 : leftKey > rightKey ? 1 : 0;
  });

  const pairs: AttendanceCorrectionBreakDraft[] = [];
  let openStart: AttendanceEvent | null = null;
  let index = 0;
  for (const event of ordered) {
    const type = (event.eventType || '').toUpperCase();
    if (type === 'BREAK_OUT') {
      if (openStart) {
        pairs.push(
          existingBreak(`existing-${index}`, clockFromEvent(openStart), ''),
        );
        index += 1;
      }
      openStart = event;
    } else if (type === 'BREAK_IN') {
      const endTime = clockFromEvent(event);
      if (openStart) {
        pairs.push(
          existingBreak(`existing-${index}`, clockFromEvent(openStart), endTime),
        );
        index += 1;
        openStart = null;
      } else {
        pairs.push(existingBreak(`existing-${index}`, '', endTime));
        index += 1;
      }
    }
  }
  if (openStart) {
    pairs.push(
      existingBreak(`existing-${index}`, clockFromEvent(openStart), ''),
    );
  }
  return pairs;
}

function withFirstBreakFields(
  draft: Omit<
    AttendanceCorrectionDraft,
    | 'breakStartTime'
    | 'breakEndTime'
    | 'breakStartLocked'
    | 'breakEndLocked'
  >,
): AttendanceCorrectionDraft {
  const first = draft.breaks[0];
  return {
    ...draft,
    breakStartLocked: Boolean(first?.startLocked),
    breakEndLocked: Boolean(first?.endLocked),
    breakStartTime: first?.startTime ?? '',
    breakEndTime: first?.endTime ?? '',
  };
}

export function draftFromAttendanceDetails(
  details: AttendanceDetails | null,
): AttendanceCorrectionDraft {
  if (!details) {
    return withFirstBreakFields({
      blocked: false,
      blockReason: null,
      checkInLocked: false,
      checkOutLocked: false,
      checkInTime: '',
      checkOutTime: '',
      breaks: [emptyBreak('break-0')],
      removedBreaks: [],
    });
  }

  const checkInTime =
    timestampToClock(details.checkIn.localTimestamp) ??
    timestampToClock(details.checkIn.timestamp) ??
    clockFromEvent(firstEvent(details.events, 'CHECK_IN'));
  const checkOutTime =
    timestampToClock(details.checkOut.localTimestamp) ??
    timestampToClock(details.checkOut.timestamp) ??
    clockFromEvent(firstEvent(details.events, 'CHECK_OUT'));
  const breaks = breakPairsFromEvents(details.events);
  const resolvedBreaks = breaks.length > 0 ? breaks : [emptyBreak('break-0')];

  return withFirstBreakFields({
    blocked: false,
    blockReason: null,
    checkInLocked: false,
    checkOutLocked: false,
    checkInTime,
    checkOutTime,
    breaks: resolvedBreaks,
    removedBreaks: [],
  });
}

export function addCorrectionBreak(
  draft: AttendanceCorrectionDraft,
): AttendanceCorrectionDraft {
  return withFirstBreakFields({
    ...draft,
    breaks: [
      ...draft.breaks,
      emptyBreak(`break-${draft.breaks.length}-${Date.now()}`),
    ],
  });
}

export function removeCorrectionBreak(
  draft: AttendanceCorrectionDraft,
  key: string,
): AttendanceCorrectionDraft {
  const target = draft.breaks.find(item => item.key === key);
  if (!target || target.startLocked || target.endLocked) return draft;
  if (draft.breaks.length <= 1) return draft;
  // A recorded break is removed only through this explicit list; approval
  // never drops a break just because the form no longer shows it.
  const removedBreaks = target.existingStartTime
    ? [
        ...draft.removedBreaks,
        {
          startTime: target.existingStartTime,
          endTime: target.existingEndTime,
        },
      ]
    : draft.removedBreaks;
  return withFirstBreakFields({
    ...draft,
    breaks: draft.breaks.filter(item => item.key !== key),
    removedBreaks,
  });
}

export function updateCorrectionBreak(
  draft: AttendanceCorrectionDraft,
  key: string,
  patch: Partial<Pick<AttendanceCorrectionBreakDraft, 'startTime' | 'endTime'>>,
): AttendanceCorrectionDraft {
  return withFirstBreakFields({
    ...draft,
    breaks: draft.breaks.map(item => {
      if (item.key !== key) return item;
      return {
        ...item,
        startTime: item.startLocked
          ? item.startTime
          : (patch.startTime ?? item.startTime),
        endTime: item.endLocked
          ? item.endTime
          : (patch.endTime ?? item.endTime),
      };
    }),
  });
}

export function correctionBreaksPayload(
  breaks: AttendanceCorrectionBreakDraft[],
): AttendanceCorrectionBreak[] {
  return breaks.map(item => {
    const payload: AttendanceCorrectionBreak = {
      startTime: item.startTime.trim(),
      endTime: item.endTime.trim(),
    };
    if (item.existingStartTime) {
      payload.existingStartTime = item.existingStartTime;
      payload.existingEndTime = item.existingEndTime;
    }
    return payload;
  });
}

/**
 * Recorded breaks to remove: rows removed from the form, and recorded rows
 * whose start and end were both cleared.
 */
export function correctionRemovedBreaksPayload(
  draft: Pick<AttendanceCorrectionDraft, 'breaks' | 'removedBreaks'>,
): AttendanceCorrectionRemovedBreak[] {
  const cleared = draft.breaks
    .filter(
      item =>
        item.existingStartTime &&
        !item.startTime.trim() &&
        !item.endTime.trim(),
    )
    .map(item => ({
      startTime: item.existingStartTime as string,
      endTime: item.existingEndTime,
    }));
  return [...draft.removedBreaks, ...cleared];
}

export function joinedCorrectionBreakTimes(
  breaks: AttendanceCorrectionBreakDraft[] | AttendanceCorrectionBreak[],
): { breakStartTime: string; breakEndTime: string } {
  return {
    breakStartTime: breaks.map(item => item.startTime.trim()).join(','),
    breakEndTime: breaks.map(item => item.endTime.trim()).join(','),
  };
}

export function displayCorrectionBreaks(input: {
  breaks?: unknown;
  breakStartTime?: string | null;
  breakEndTime?: string | null;
}): { startTime: string; endTime: string }[] {
  let raw: unknown = input.breaks;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.startsWith('[')) {
      try {
        raw = JSON.parse(trimmed) as unknown;
      } catch {
        raw = null;
      }
    }
  }
  if (Array.isArray(raw)) {
    const parsed = raw
      .map(item => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Record<string, unknown>;
        const startTime =
          typeof row.startTime === 'string'
            ? row.startTime.trim()
            : typeof row.breakStartTime === 'string'
              ? row.breakStartTime.trim()
              : '';
        const endTime =
          typeof row.endTime === 'string'
            ? row.endTime.trim()
            : typeof row.breakEndTime === 'string'
              ? row.breakEndTime.trim()
              : '';
        if (!startTime || !endTime) return null;
        return { startTime, endTime };
      })
      .filter((item): item is { startTime: string; endTime: string } =>
        Boolean(item),
      );
    if (parsed.length > 0) return parsed;
  }
  if (input.breakStartTime && input.breakEndTime) {
    return [
      { startTime: input.breakStartTime, endTime: input.breakEndTime },
    ];
  }
  return [];
}

/** Removed breaks from request details: an array or its JSON text. */
export function displayRemovedCorrectionBreaks(
  value: unknown,
): AttendanceCorrectionRemovedBreak[] {
  let raw = value;
  if (typeof raw === 'string' && raw.trim().startsWith('[')) {
    try {
      raw = JSON.parse(raw) as unknown;
    } catch {
      raw = null;
    }
  }
  if (!Array.isArray(raw)) return [];
  return raw.flatMap(item => {
    if (!item || typeof item !== 'object') return [];
    const row = item as Record<string, unknown>;
    const startTime =
      typeof row.startTime === 'string' ? row.startTime.trim() : '';
    const endTime = typeof row.endTime === 'string' ? row.endTime.trim() : '';
    if (!startTime) return [];
    return [{ startTime, endTime: endTime || null }];
  });
}

export function validateAttendanceCorrectionTimes(input: {
  checkInTime: string;
  checkOutTime: string;
  breakStartTime?: string;
  breakEndTime?: string;
  breaks?: { startTime: string; endTime: string }[];
  checkInLocked: boolean;
}): string | null {
  const checkIn = input.checkInTime.trim();
  const checkOut = input.checkOutTime.trim();
  const breaks =
    input.breaks && input.breaks.length > 0
      ? input.breaks
      : [
          {
            startTime: (input.breakStartTime ?? '').trim(),
            endTime: (input.breakEndTime ?? '').trim(),
          },
        ];

  const enteredBreaks = breaks.filter(
    item => item.startTime.trim() || item.endTime.trim(),
  );

  if (!checkIn) {
    return input.checkInLocked
      ? 'Existing check-in time could not be loaded.'
      : 'Check-in time is required.';
  }

  const checkInMinutes = parseClockToMinutes(checkIn);
  if (checkInMinutes === null) {
    return 'Choose a valid check-in time.';
  }

  let checkOutMinutes: number | null = null;
  if (checkOut) {
    checkOutMinutes = parseClockToMinutes(checkOut);
    if (checkOutMinutes === null) {
      return 'Choose a valid check-out time.';
    }
    if (checkOutMinutes <= checkInMinutes) {
      return 'Check-out time must be after check-in time.';
    }
  }

  let previousEnd: number | null = null;
  for (let index = 0; index < enteredBreaks.length; index += 1) {
    const start = enteredBreaks[index].startTime.trim();
    const end = enteredBreaks[index].endTime.trim();
    const label = enteredBreaks.length > 1 ? `Break ${index + 1}` : 'Break';
    if (!start || !end) {
      return `Enter both the ${label.toLowerCase()} start and end times, or leave both empty.`;
    }
    const startMinutes = parseClockToMinutes(start);
    const endMinutes = parseClockToMinutes(end);
    if (startMinutes === null || endMinutes === null) {
      return 'Choose valid check-in, break, and check-out times.';
    }
    if (startMinutes < checkInMinutes) {
      return `${label} start must be after check-in.`;
    }
    if (endMinutes <= startMinutes) {
      return `${label} end must be after break start.`;
    }
    if (checkOutMinutes !== null && endMinutes > checkOutMinutes) {
      return `${label} end must be before check-out.`;
    }
    if (previousEnd !== null && startMinutes < previousEnd) {
      return `${label} cannot overlap the previous break.`;
    }
    previousEnd = endMinutes;
  }
  return null;
}
